// src/context/BlogContext.js

import React, { createContext, useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  startAfter,
  limit,
  getDocs,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

export const BlogContext = createContext();

// Define the number of blogs to show per page.
const PAGE_SIZE = 5;

export const BlogProvider = ({ children }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true); // Tracks if there are more blogs to fetch
  const [lastVisible, setLastVisible] = useState(null); // The cursor for pagination
  const [isSearching, setIsSearching] = useState(false); // Differentiate search from normal pagination

  /**
   * Fetches the initial set of blogs.
   */
  const addBlogToState = (newBlog) => {
    setBlogs(prevBlogs => [newBlog, ...prevBlogs]);
  };

  const deleteBlogFromState = (blogId) => {
    setBlogs(prevBlogs => prevBlogs.filter(blog => blog.id !== blogId));
  };

  const fetchInitialBlogs = useCallback(async () => {
    setLoading(true);
    setIsSearching(false);
    try {
      // Query for PAGE_SIZE + 1 documents to see if a next page exists.
      const q = query(
        collection(db, "blogs"),
        orderBy("date", "desc"),
        limit(PAGE_SIZE + 1)
      );
      const documentSnapshots = await getDocs(q);

      const docs = documentSnapshots.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // If we fetched fewer docs than PAGE_SIZE + 1, there are no more pages.
      if (docs.length <= PAGE_SIZE) {
        setHasMore(false);
        setBlogs(docs);
      } else {
        // We have more pages. Set the cursor to the last item on the *current* page.
        setHasMore(true);
        setLastVisible(documentSnapshots.docs[PAGE_SIZE - 1]);
        // And only add PAGE_SIZE items to the state.
        setBlogs(docs.slice(0, PAGE_SIZE));
      }
    } catch (error) {
      console.error("Error fetching initial blogs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run the initial fetch when the component mounts.
  useEffect(() => {
    fetchInitialBlogs();
  }, [fetchInitialBlogs]);

  /**
   * Fetches the next page of blogs starting after the last visible document.
   */
  const fetchNextPage = async () => {
    if (loading || !hasMore) return 0; // Don't fetch if already loading or no more data

    setLoading(true);
    try {
      const q = query(
        collection(db, "blogs"),
        orderBy("date", "desc"),
        startAfter(lastVisible), // The key to pagination!
        limit(PAGE_SIZE + 1)
      );
      const documentSnapshots = await getDocs(q);

      const newDocs = documentSnapshots.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (newDocs.length <= PAGE_SIZE) {
        setHasMore(false);
      } else {
        setLastVisible(documentSnapshots.docs[PAGE_SIZE - 1]);
      }

      const docsToAdd = newDocs.slice(0, PAGE_SIZE);
      setBlogs((prevBlogs) => [...prevBlogs, ...docsToAdd]);

      setLoading(false);
      return docsToAdd.length; // Return how many new blogs were added
    } catch (error) {
      console.error("Error fetching next page:", error);
      setLoading(false);
      return 0;
    }
  };

  /**
   * Searches blogs by title. Resets pagination.
   */
  const searchBlogs = async (searchTerm) => {
    // If search is empty, just refetch the initial blogs.
    if (!searchTerm.trim()) {
      fetchInitialBlogs();
      return;
    }

    setLoading(true);
    setIsSearching(true);
    setHasMore(false); // Disable pagination during search display
    try {
      // Firestore does not support case-insensitive 'contains' queries efficiently.
      // This is a common "starts with" workaround.
      const q = query(
        collection(db, "blogs"),
        orderBy("title"),
        where("title", ">=", searchTerm),
        where("title", "<=", searchTerm + "\uf8ff")
      );

      const querySnapshot = await getDocs(q);
      const searchResults = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBlogs(searchResults);
    } catch (error) {
      console.error("Error searching blogs:", error);
    } finally {
      setLoading(false);
    }
  };


  const value = {
    blogs,
    setBlogs,
    loading,
    hasMore,
    fetchNextPage,
    searchBlogs,
    isSearching,
    addBlogToState,
    deleteBlogFromState
  };

  return <BlogContext.Provider value={value}>{children}</BlogContext.Provider>;
};